import React, { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Grid,
  Box,
  Button,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import CreateIcon from "@mui/icons-material/Create";
import styles from "./styles.module.css";
import CircleLoader from "react-spinners/CircleLoader";
import ExportExcelButton from "../../components/ExportExcelButton";

function CellEllipsis({ value }) {
  const text =
    value == null || value === "" ? "—" : String(value);
  return (
    <Tooltip title={text} placement="top-start" enterDelay={400}>
      <span className={styles.celulaEllipsis}>{text}</span>
    </Tooltip>
  );
}

const TabelaDinamica = ({
  data,
  columns,
  loading,
  orderColumn,
  onEditButtonClick,
  onDeleteButtonClick,
  disableEdit,
  disableDelete,
  exportFilename,
  exportData,
  functionExportPDF,
  msgTable,
  extraProps,
  fixedLayout = false,
  actionsColumnWidth = "96px",
}) => {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState(orderColumn);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);


  useEffect(() => {
    if (page * rowsPerPage > data.length) {
      setPage(Math.floor(data.length / rowsPerPage));
    }
  }, [data]);

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  function ascendingComparator(a, b, orderBy) {
    const emptyCompare = emptyComparator(a, b, order);
    if (emptyCompare) return emptyCompare;

    if (b[orderBy] < a[orderBy]) {
      return 1;
    }
    if (b[orderBy] > a[orderBy]) {
      return -1;
    }
    return 0;
  }

  function descendingComparator(a, b, orderBy) {
    const emptyCompare = emptyComparator(a, b, order);
    if (emptyCompare) return emptyCompare;

    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function emptyComparator(a, b, order) {
    if (a[orderBy] === "" && b[orderBy] === "") {
      return 0;
    }

    if (a[orderBy] === "" || b[orderBy] === "") {
      return a[orderBy] ? -1 : 1;
    }

    return undefined;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => ascendingComparator(a, b, orderBy);
  }

  const visibleRows = React.useMemo(
    () =>
      data
        .slice()
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [data, order, orderBy, page, rowsPerPage]
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function labelDisplayedRows({ from, to, count }) {
    return `${from}–${to} de ${count !== -1 ? count : `muitos`}`;
  }

  const handleFormatCell = (cell, type) => {
    const formatNumber = (skipToFixed = false, toFixed = 2) => {
      let valorFormatado = Number(cell);
      if (!skipToFixed) {
        valorFormatado = valorFormatado.toFixed(toFixed);
      }
      valorFormatado = String(valorFormatado).replace(".", ",");
      let [integerPart, decimalPart] = valorFormatado.split(',');
      let formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return `${formattedIntegerPart}${
        (decimalPart)
        ? ',' + decimalPart
        : ''
      }`;
    };

    switch (true) {
      case cell === true:
        return "Sim";
      case cell === false:
        return "Não";
      case cell instanceof Date:
        return cell.toLocaleDateString("pt-BR", { timeZone: "GMT" });
      case type === "porcentagem":
        return formatNumber(true) + " %";
      case type === "monetario":
        return "R$ " + formatNumber();
      case type === "numerico":
        return formatNumber();

      default:
        return cell;
    }
  };

  const handleAlignColumn = (type) => {
    switch (type) {
      case "porcentagem":
        return "right";
      case "monetario":
        return "right";
      case "numerico":
        return "right";
      default:
        return "left";
    }
  };

  const getColumnWidthStyle = (width) =>
    width ? { width, maxWidth: fixedLayout ? 0 : width } : undefined;

  const renderDefaultCell = (column, row) => {
    const formatted = handleFormatCell(row[column.field], column?.type);
    if (fixedLayout && column.ellipsis !== false) {
      return <CellEllipsis value={formatted} />;
    }
    return formatted;
  };

  const showActions = !(disableEdit && disableDelete);

  return (
    <div className={styles.backgroundTable}>
      {loading ? (
        <div
          className={styles.tabelaLoading}
          style={{
            display: "flex",
            paddingTop: "10%",
            paddingLeft: "45%",
          }}
        >
          <CircleLoader
            color="#18316e"
            loading={loading}
            size={100}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </div>
      ) : (
        <Box className={styles.tabelaGeral}>
          <TableContainer
            className={fixedLayout ? styles.tableContainerFixa : undefined}
          >
            <Table
              aria-label="dynamic table"
              className={fixedLayout ? styles.tabelaFixa : undefined}
            >
              <TableHead>
                <TableRow className={styles.tableHeader}>
                  {columns.map((column) => (
                    <TableCell
                      className={styles.tabelaHeader}
                      align={handleAlignColumn(column?.type)}
                      key={column.id}
                      style={getColumnWidthStyle(column.width)}
                    >
                      {column.no_ordering ? (
                        <div sx={{ whiteSpace: "pre-line" }}>
                          {column.label}
                        </div>
                      ) : (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={createSortHandler(column.id)}
                          sx={{
                            "& .MuiTableSortLabel-icon": {
                              color: "white !important",
                            },
                            "&.Mui-active": {
                              color: "white !important",
                            },
                            "&:hover": {
                              color: "white !important",
                            },
                          }}
                        >
                          <div sx={{ whiteSpace: "pre-line" }}>
                            {column.label}
                          </div>
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                  {showActions && (
                    <TableCell
                      className={`${styles.tabelaHeader} ${fixedLayout ? styles.colunaAcoes : ""}`}
                      style={fixedLayout ? { width: actionsColumnWidth } : undefined}
                    >
                      Ações
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow className={styles.tabelaBody}>
                    <TableCell colSpan={columns.length + 1}>
                      {msgTable || "Nenhum registro encontrado"}
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((row, idx) => (
                    <TableRow key={idx}>
                      {columns.map((column) =>
                        column.render ? (
                          <TableCell
                            key={column.id}
                            className={`${styles.customCell} ${
                              fixedLayout ? styles.celulaTruncada : ""
                            }`}
                            align={handleAlignColumn(column?.type)}
                            style={getColumnWidthStyle(column.width)}
                          >
                            {column.render(row)}
                          </TableCell>
                        ) : (
                          <TableCell
                            key={column.id}
                            align={handleAlignColumn(column?.type)}
                            className={`${styles.customCell} ${
                              fixedLayout ? styles.celulaTruncada : ""
                            }`}
                            style={
                              fixedLayout
                                ? getColumnWidthStyle(column.width)
                                : { whiteSpace: "nowrap" }
                            }
                          >
                            {renderDefaultCell(column, row)}
                          </TableCell>
                        )
                      )}
                      {showActions && (
                        <TableCell
                          className={`${styles.customCell} ${
                            fixedLayout ? styles.colunaAcoes : ""
                          }`}
                          style={
                            fixedLayout ? { width: actionsColumnWidth } : undefined
                          }
                        >
                          <div className={styles.boxAcoes}>
                            {!disableEdit && (
                              <IconButton
                                aria-label="edit"
                                className={styles.iconeEditar}
                                onClick={() => onEditButtonClick(row)}
                              >
                                <CreateIcon />
                              </IconButton>
                            )}
                            {!disableDelete && (
                              <IconButton
                                aria-label="delete"
                                className={styles.iconeExcluir}
                                onClick={() => onDeleteButtonClick(row)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              hidden={data.length === 0}
              className={styles.paginacaoTabela}
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={data.length}
              labelRowsPerPage={"Resultados por página:"}
              labelDisplayedRows={labelDisplayedRows}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
              sx={{
                "& .MuiTablePagination-actions button": {
                  color: "white !important",
                },
                "& .MuiTablePagination-selectIcon": {
                  color: "white !important",
                },
                "& .Mui-disabled": {
                  display: "none !important",
                },
              }}
            />
          </TableContainer>
          {(exportData || extraProps) && data.length > 0 && (
            <Grid
              container
              spacing={1}
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
              }}
            >
              {exportData && data.length > 0 && (
                <>
                  <Grid item xs={2} className={styles.buttonPadding}>
                    <ExportExcelButton
                      data={exportData}
                      filename={exportFilename}
                    />
                  </Grid>
                  {functionExportPDF && (
                    <Grid item xs={2} className={styles.buttonPadding}>
                      <Button
                        onClick={functionExportPDF}
                        size="small"
                        className={styles.btnPDF}
                      >
                        Download PDF
                      </Button>
                    </Grid>
                  )}
                </>
              )}
              {extraProps}
            </Grid>
          )}
        </Box>
      )}
    </div>
  );
};

export default TabelaDinamica;
